<template>
  <BasicBlock :title="$t('order_intent_info.block.title')">
    <div
      :class="
        singleColumn
          ? 'f-grid f-grid-cols-1 f-gap-2 sm:f-gap-3'
          : 'f-grid f-grid-cols-1 sm:f-grid-cols-2 f-gap-2 sm:f-gap-3'
      "
    >
      <DefaultInput
        name="email"
        type="email"
        queryParam="email"
        autocomplete="email"
        :label="$t('basic_info.email.label')"
        :showInput="true"
        :showInputInFullForm="true"
        :singleColumn="singleColumn"
        :isFullForm="fullForm"
        :rules="{
          required: true,
          email: true,
        }"
        @input="$emit('basicDataChanged', $event)"
      ></DefaultInput>

      <BasePhone
        name="phone"
        type="tel"
        queryParam="mobil"
        autocomplete="tel"
        :rules="emptyRules"
        :formResource="formResource"
        :label="$t('basic_info.phone.label')"
        :showInput="fieldSettings.phone.show"
        :showInputInFullForm="false"
        :isFullForm="fullForm"
        :required="fieldSettings.phone.required"
        :singleColumn="singleColumn"
        :country="formData.address.country"
        @input="$emit('basicDataChanged', $event)"
      ></BasePhone>

      <DefaultInput
        name="first_name"
        type="text"
        queryParam="name"
        autocomplete="given-name"
        :label="$t('basic_info.first_name.label')"
        :showInput="fieldSettings.name.show"
        :showInputInFullForm="true"
        :isFullForm="fullForm"
        :rules="nameRules"
        :singleColumn="singleColumn"
        @input="$emit('basicDataChanged', $event)"
      ></DefaultInput>

      <DefaultInput
        name="last_name"
        type="text"
        queryParam="surname"
        autocomplete="family-name"
        :label="$t('basic_info.last_name.label')"
        :showInput="fieldSettings.name.show"
        :showInputInFullForm="true"
        :isFullForm="fullForm"
        :rules="nameRules"
        :singleColumn="singleColumn"
        @input="$emit('basicDataChanged', $event)"
      ></DefaultInput>
    </div>

    <p
      v-for="(purpose, index) in purposes"
      v-bind:key="index + '-purpose'"
      class="f-mb-2"
    >
      <Purpose
        :purpose="purpose"
        @purposeChanged="$emit('purposeChanged', $event)"
      ></Purpose>
    </p>

    <div class="f-text-center f-w-full f-mt-4 f-pt-5">
      <button
        type="button"
        @click="$emit('triggerOrderIntent', $event)"
        class="fapi-submit-button"
        :disabled="isSubmitting"
        v-show="!isSubmitting"
      >
        {{ submitLabel }}
      </button>
      <div class="f-text-center f-mt-2 fapi-sending" v-if="isSubmitting">
        <svg
          class="f-animate-spin f-h-11 f-w-11 f-mx-auto"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="f-opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="f-opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <p class="f-mt-2 fapi-sending">
          {{ $t("order_intent_info.submit.processing") }}
        </p>
      </div>
    </div>
  </BasicBlock>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import FormResource from "@/module/Form/Resources/FormResource";
import BasicBlock from "@/components/Form/BasicBlock.vue";
import DefaultInput from "@/components/Form/Basic/Inputs/DefaultInput.vue";
import FormData from "@/module/FormData";
import TemplateSettings from "@/module/Form/Resources/TemplateSettings";
import FieldSettings from "@/module/Form/Resources/FieldSettings";
import {
  isCssStyleOrNull,
  isStringOrNull,
} from "@/module/ComponentPropsValidations/validations";
import CssStyle from "@/module/Form/Resources/CssStyle";
import Purpose from "@/components/Form/Result/Purpose.vue";

interface ComponentData {
  isCompany: boolean;
  fullForm: boolean;
  purposes: Array<object>;
}

export default Vue.extend({
  name: "OrderIntentData",

  components: {
    Purpose,
    BasePhone: () =>
      import(
        /* webpackChunkName: "phone-input" */ "@/components/Form/Basic/Inputs/BasePhone.vue"
      ),
    DefaultInput,
    BasicBlock,
  },

  props: {
    formResource: {
      type: Object as PropType<FormResource>,
      required: true,
    },
    styling: {
      type: null as unknown as PropType<CssStyle | null>,
      required: true,
      validator: isCssStyleOrNull,
    },
    templateSettings: {
      type: Object as PropType<TemplateSettings>,
      required: true,
    },
    country: {
      required: true,
      validator: isStringOrNull,
    },
    formData: {
      type: Object as PropType<FormData>,
      required: true,
    },
    fieldSettings: {
      type: Object as PropType<FieldSettings>,
      required: true,
    },
    isSubmitting: {
      type: Boolean,
      required: true,
    },
  },

  data(): ComponentData {
    return {
      isCompany: false,
      fullForm: false,
      purposes: [],
    };
  },

  computed: {
    submitLabel(): string {
      if (
        this.styling === null ||
        !this.styling.enabled ||
        !this.styling.button_step_one_label
      ) {
        return this.$t("order_intent_info.submit.label").toString();
      }

      return (
        this.styling.button_step_one_label ||
        this.$t("order_intent_info.submit.label").toString()
      );
    },

    isFullForm(): boolean {
      return (
        (this.fieldSettings.name.required ||
          this.fieldSettings.company.required) &&
        this.fieldSettings.address.required
      );
    },

    singleColumn(): boolean {
      return (
        this.templateSettings.container_settings.billing.layout.column ===
        "single"
      );
    },

    nameRules(): object {
      return {
        required:
          !this.isCompany &&
          (this.fieldSettings.name.required || this.fullForm) &&
          !this.fieldSettings.company.required,
      };
    },

    emptyRules(): object {
      return {};
    },
  },

  mounted(): void {
    this.$emit("updateBasicData", {
      full_invoice: this.isFullForm,
    });

    for (const item of this.formResource.purposes) {
      if (item.step === "intent") {
        this.purposes.push(item);
      }
    }
  },
});
</script>
